import { finalize, map, shareReplay, tap } from 'rxjs/operators';
import { BehaviorSubject, Observable } from 'rxjs';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { PostContent } from '../../_models/cms/postContent';
import { Post } from '../../_models/cms/post';
import { environment } from '../../../../environments/environment';
import { User } from '../../_models';
import { RestrictedPostInteraction } from '../../_models/restrictedPostInteraction';
import {
  UserGroupGet,
  UserGroupGetLiteUserGroupGetLite,
  UserGroupPost,
} from '../../_models/userGroups';

@Injectable()
export class PostService {
  private apiBaseUrl = environment.apiBaseUrl;
  postContentEditFormItem: BehaviorSubject<PostContent> = new BehaviorSubject<PostContent>(null);
  postItem: BehaviorSubject<Post> = new BehaviorSubject<Post>(null);

  // Use a BehaviorSubject to cache the list of posts. Initialize with null or an empty array.
  private trainingList$ = new BehaviorSubject<Post[] | null>(null);

  // This will hold the observable of an in-progress API call.
  private trainingFetchInProgress$: Observable<Post[]> | null = null;

  constructor(private http: HttpClient) {}

  getPostContentEditFormItem = () => {
    return this.postContentEditFormItem.asObservable();
  };

  updatePostContentEditFormItem(postContent: PostContent) {
    this.postContentEditFormItem.next(postContent);
  }

  getPostItem = () => {
    return this.postItem.asObservable();
  };

  updatePostItem(post: Post) {
    this.postItem.next(post);
  }

  getPosts(): Observable<Post[]> {
    return this.http
      .get<Post[]>(this.apiBaseUrl + 'api/private/v1/cms/posts')
      .pipe(map((response) => response));
  }

  getNewsBySaaS(brand: string): Observable<Post[]> {
    return this.http
      .get<Post[]>(this.apiBaseUrl + 'api/private/v1/cms/restrictedPosts?saasCode=' + brand)
      .pipe(map((response) => response));
  }

  /**
   * Fetches the training posts from the API.
   * This method is now private as it's the core fetch logic that the public method will wrap.
   *
   * @returns Observable<Post[]> - The fetched posts.
   */
  private fetchTrainingPosts(): Observable<Post[]> {
    return this.http.get<Post[]>(this.apiBaseUrl + 'api/private/v1/cms/restrictedPosts').pipe(
      // Use tap for side-effects like updating a BehaviorSubject.
      // This ensures the cache is updated as soon as data arrives.
      tap((posts) => {
        this.trainingList$.next(posts);
      })
    );
  }

  /**
   * Retrieves the training posts, either from cache or by initiating an API request.
   * This is the new public method that components should call.
   *
   * - If `forceFetch` is true, it always fetches fresh data from the API.
   * - If data is already cached, it returns the cached data.
   * - If no data is cached, it triggers a new fetch.
   * - If a fetch is already in progress, it returns the in-progress observable.
   *
   * @param forceFetch - Optional. Defaults to `false`. Set to `true` to bypass cache and fetch fresh data.
   * @returns Observable<Post[]> - An observable that emits the training posts.
   */
  getTrainingPosts(forceFetch: boolean = false): Observable<Post[]> {
    const currentValue = this.trainingList$.getValue();

    // 1. Return cached data if available and no forceFetch is requested
    if (!forceFetch && currentValue) {
      return this.trainingList$.asObservable() as Observable<Post[]>;
    }

    // 2. Return in-progress fetch if one is already happening
    if (this.trainingFetchInProgress$) {
      return this.trainingFetchInProgress$;
    }

    // 3. Start a new fetch
    this.trainingFetchInProgress$ = this.fetchTrainingPosts().pipe(
      finalize(() => {
        // When the observable completes (or errors), clear the in-progress flag.
        this.trainingFetchInProgress$ = null;
      }),
      // shareReplay(1) ensures that concurrent subscribers share the same
      // HTTP request and that late subscribers get the last emitted value.
      shareReplay(1)
    );

    return this.trainingFetchInProgress$;
  }

  getPost(id: number): Observable<Post> {
    return this.http
      .get<Post>(this.apiBaseUrl + 'api/private/v1/cms/posts/' + id)
      .pipe(map((response) => response));
  }

  getPostsWithInteraction(): Observable<RestrictedPostInteraction[]> {
    return this.http
      .get<
        RestrictedPostInteraction[]
      >(this.apiBaseUrl + 'api/private/v1/cms/restrictedPostInteractions/')
      .pipe(map((response) => response));
  }

  sendInteraction(post): Observable<RestrictedPostInteraction> {
    return this.http
      .post<RestrictedPostInteraction>(
        this.apiBaseUrl + 'api/private/v1/cms/restrictedPostInteractions/',
        post
      )
      .pipe(map((response) => response));
  }

  savePost(postItem: Post): Observable<Post> {
    const post = { ...postItem };
    delete post.lastUpdated;
    delete post.dateCreated;

    const companySelected = JSON.parse(localStorage.getItem('companySelected'));
    post.companyId = companySelected ? companySelected.id : null;

    if (post.user != null && post.user.id != null) {
      post.user = { id: post.user.id } as User;
    }

    if (post.id) {
      return this.http.put<Post>(this.apiBaseUrl + 'api/private/v1/cms/posts/' + post.id, post);
    } else {
      return this.http.post<Post>(this.apiBaseUrl + 'api/private/v1/cms/posts/', post);
    }
  }

  deletePost(post: Post): Observable<Post> {
    return this.http.delete(this.apiBaseUrl + 'api/private/v1/cms/posts/' + post.id).pipe(
      map(() => {
        return { id: post.id } as Post;
      })
    );
  }

  getUserGroups(): Observable<UserGroupGetLiteUserGroupGetLite[]> {
    return this.http
      .get<
        UserGroupGetLiteUserGroupGetLite[]
      >(this.apiBaseUrl + 'api/private/v1/cms/userGroupCommunication')
      .pipe(map((response) => response));
  }

  getUserGroup(userGroup: UserGroupGetLiteUserGroupGetLite): Observable<UserGroupGet> {
    return this.http
      .get<UserGroupGet>(
        this.apiBaseUrl + 'api/private/v1/cms/userGroupCommunication/' + userGroup.id
      )
      .pipe(map((response) => response));
  }

  saveUserGroup(userGroup: UserGroupPost, groupId: string = null): Observable<UserGroupPost> {
    if (groupId) {
      return this.http
        .put<UserGroupPost>(
          this.apiBaseUrl + 'api/private/v1/cms/userGroupCommunication/' + groupId,
          userGroup
        )
        .pipe(map((response) => response));
    } else {
      return this.http
        .post<UserGroupPost>(
          this.apiBaseUrl + 'api/private/v1/cms/userGroupCommunication/',
          userGroup
        )
        .pipe(map((response) => response));
    }
  }

  deleteUserGroup(
    userGroup: UserGroupGetLiteUserGroupGetLite
  ): Observable<UserGroupGetLiteUserGroupGetLite> {
    return this.http
      .delete(this.apiBaseUrl + 'api/private/v1/cms/userGroupCommunication/' + userGroup.id)
      .pipe(
        map(() => {
          return { id: userGroup.id } as UserGroupGetLiteUserGroupGetLite;
        })
      );
  }
}
